/*
 *
 *  * Copyright(c) 2021 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { appConstants } from './common/app-constants';
import { HttpService } from './core/http/http.service';
import { UtilService } from '@app/util/util.service';
import * as moment from 'moment';
import { Logger } from '@core/lib/services';

const log = new Logger('AppDataResolver');

@Injectable()
export class AppDataResolver {
  systemProp;
  constructor(private httpClient: HttpService, private translate: TranslateService, private utilService: UtilService) {}

  loadData() {
    const httpOptions = {
      headers: new HttpHeaders({
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        'External-Login': window.location.search.indexOf(`${appConstants.externalApp}=`) > -1 ? 'true' : 'false'
      })
    };
    return new Promise<void>((resolve, _reject) => {
      this.httpClient
        .get(appConstants.appURLs.systemProperties, httpOptions)
        .toPromise()
        .then(data => {
          this.utilService.setSystemProp(data);
          const desktopIntegrations = data[appConstants.desktopIntegrations];
          if (desktopIntegrations) {
            data[appConstants.desktopIntegrations] = JSON.parse(desktopIntegrations);
          }
          this.utilService.setAppBaseUrl(data[appConstants.system_properties.APP_BASE_URL]);

          /* Locale */
          const supportedLangs = data[appConstants.system_properties.SUPPORTED_LANGUAGES];
          let locale: string = data[appConstants.system_properties.SYSTEM_DEFAULT_LANGUAGE];
          if (locale === 'en' || supportedLangs.indexOf(locale) <= -1) {
            locale = appConstants.locale.default;
          }

          /* Moment Settings
           * Note:
           *  Call moment.updateLocale() only once.
           *  Subsequent calls does not update the fields registered as function in parentLocale.
           */
          moment.locale(locale);
          const customMomentParams = {};
          const firstDayOfWeek = appConstants.locale.momentOverride.FirstDayOfWeek[locale.toLowerCase()];
          customMomentParams['week'] = { dow: firstDayOfWeek, doy: 6 + firstDayOfWeek };
          if (appConstants.locale.momentOverride.monthsShort[locale.toLowerCase()]) {
            customMomentParams['monthsShort'] = appConstants.locale.momentOverride.monthsShort[locale.toLowerCase()];
          }
          moment.updateLocale(moment.locale(), customMomentParams);

          /* ngxTranslate Settings */
          this.translate.addLangs(JSON.parse(supportedLangs));
          this.translate.setDefaultLang(appConstants.locale.default);
          this.translate.use(locale);

          resolve();
        })
        .catch(ex => {
          log.error('error occurred while getting system properties', ex);
          _reject();
        });
    });
  }
}
